import { useState, useEffect, useRef } from "react"
import { Search, X, Users, UserCircle } from "lucide-react"
import { getUsers } from "../../services/users"
import { getContacts } from "../../services/contacts"

interface TeamMember {
  id: number
  username: string
  email?: string
}

interface Contact {
  id: number
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
}

interface ParticipantSelectorProps {
  selectedTeamMembers: number[]
  selectedContact: number | null
  onTeamMembersChange: (ids: number[]) => void
  onContactChange: (id: number | null) => void
}

export function ParticipantSelector({
  selectedTeamMembers,
  selectedContact,
  onTeamMembersChange,
  onContactChange,
}: ParticipantSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch team members and contacts on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [usersRes, contactsRes] = await Promise.all([
          getUsers(),
          getContacts(),
        ])
        setTeamMembers(
          (usersRes.data || []).filter(
            (u: any) => u && typeof u.id === "number"
          )
        )
        setContacts(
          (contactsRes.data?.results || contactsRes.data || []).filter(
            (c: any) => c && typeof c.id === "number"
          )
        )
      } catch {
        // Silently fail — lists will be empty
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // Click-outside handler
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const getContactName = (c: Contact) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ")
    return name || c.company_name || `Contatto #${c.id}`
  }

  const getTeamMemberName = (m: TeamMember) => m.username

  // Filter by search query
  const queryLower = searchQuery.toLowerCase()
  const filteredTeam = teamMembers.filter(
    (m) =>
      !selectedTeamMembers.includes(m.id) &&
      (m.username.toLowerCase().includes(queryLower) ||
        m.email?.toLowerCase().includes(queryLower))
  )
  const filteredContacts = contacts.filter(
    (c) =>
      c.id !== selectedContact &&
      (getContactName(c).toLowerCase().includes(queryLower) ||
        c.email?.toLowerCase().includes(queryLower) ||
        c.company_name?.toLowerCase().includes(queryLower))
  )

  // Resolve selected items for chips
  const selectedTeamData = teamMembers.filter((m) =>
    selectedTeamMembers.includes(m.id)
  )
  const selectedContactData = contacts.find((c) => c.id === selectedContact)

  const toggleTeamMember = (id: number) => {
    if (selectedTeamMembers.includes(id)) {
      onTeamMembersChange(selectedTeamMembers.filter((x) => x !== id))
    } else {
      onTeamMembersChange([...selectedTeamMembers, id])
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-[var(--hexa-text-muted)]">Partecipanti</div>
      <div ref={dropdownRef} className="relative">
        {/* Selected chips + search input */}
        <div
          className="bg-[var(--hexa-button-cancel)] border border-[var(--hexa-border)] rounded-md p-1.5 min-h-[34px] cursor-text"
          onClick={() => setIsOpen(true)}
        >
          <div className="flex flex-wrap gap-1.5 items-center">
            {/* Team chips */}
            {selectedTeamData.map((m) => (
              <div
                key={`team-${m.id}`}
                className="participant-chip flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-medium text-white"
                style={{ backgroundColor: "var(--hexa-chip-team, #7b3fff)" }}
              >
                <span>{m.username}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleTeamMember(m.id)
                  }}
                  className="hover:bg-white/20 rounded-sm p-0.5 transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}

            {/* Contact chip */}
            {selectedContactData && (
              <div
                key={`contact-${selectedContactData.id}`}
                className="participant-chip flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-medium text-white"
                style={{ backgroundColor: "var(--hexa-chip-contact, #00d492)" }}
              >
                <span>{getContactName(selectedContactData)}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onContactChange(null)
                  }}
                  className="hover:bg-white/20 rounded-sm p-0.5 transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            )}

            {/* Search input */}
            <div className="flex-1 min-w-[120px] flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-[var(--hexa-text-muted)] flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  if (!isOpen) setIsOpen(true)
                }}
                onFocus={() => setIsOpen(true)}
                placeholder={
                  selectedTeamMembers.length === 0 && !selectedContact
                    ? "Cerca team o contatti..."
                    : "Aggiungi..."
                }
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] text-xs"
              />
            </div>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-[var(--hexa-surface)] border border-[var(--hexa-border)] rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto scrollbar-hexa">
            {loading && (
              <div className="px-3 py-2 text-xs text-[var(--hexa-text-muted)]">
                Caricamento...
              </div>
            )}

            {/* Team section */}
            {filteredTeam.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] font-semibold text-[var(--hexa-text-muted)] uppercase tracking-wider bg-[var(--hexa-surface-elevated)]">
                  <Users className="h-3 w-3 inline mr-1.5" />
                  Team
                </div>
                {filteredTeam.map((m) => (
                  <button
                    key={`opt-team-${m.id}`}
                    type="button"
                    onClick={() => toggleTeamMember(m.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--hexa-surface-hover)] transition-colors"
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                      style={{ backgroundColor: "var(--hexa-chip-team, #7b3fff)" }}
                    >
                      {getInitials(m.username)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[var(--hexa-text-primary)] truncate">
                        {m.username}
                      </div>
                      {m.email && (
                        <div className="text-[10px] text-[var(--hexa-text-muted)] truncate">
                          {m.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Contacts section */}
            {filteredContacts.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] font-semibold text-[var(--hexa-text-muted)] uppercase tracking-wider bg-[var(--hexa-surface-elevated)]">
                  <UserCircle className="h-3 w-3 inline mr-1.5" />
                  Contatti
                  {selectedContact !== null && (
                    <span className="ml-1 normal-case font-normal">(max 1)</span>
                  )}
                </div>
                {filteredContacts.map((c) => (
                  <button
                    key={`opt-contact-${c.id}`}
                    type="button"
                    onClick={() => onContactChange(c.id)}
                    disabled={selectedContact !== null}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--hexa-surface-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                      style={{ backgroundColor: "var(--hexa-chip-contact, #00d492)" }}
                    >
                      {getInitials(getContactName(c))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[var(--hexa-text-primary)] truncate">
                        {getContactName(c)}
                      </div>
                      {c.company_name && (
                        <div className="text-[10px] text-[var(--hexa-text-muted)] truncate">
                          {c.company_name}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Empty state */}
            {!loading && filteredTeam.length === 0 && filteredContacts.length === 0 && (
              <div className="px-3 py-3 text-xs text-[var(--hexa-text-muted)] text-center">
                {searchQuery ? "Nessun risultato" : "Nessun partecipante disponibile"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
